/**
 * Base de conocimiento: alta, listado y borrado de documentos que el agente
 * de IA puede consultar.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type { KnowledgeDocumentView } from '@shared/api-types.ts';
import { api, errorMessage, isAbortError } from '../api.ts';
import { formatDateTime } from '../format.ts';
import type { NoticeKind } from '../hooks.ts';
import { Chip, EmptyState, ErrorBanner, Field, SkeletonRows, Spinner } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

type State =
  | { status: 'loading' }
  | { status: 'ready'; items: KnowledgeDocumentView[] }
  | { status: 'error'; message: string };

/** La API limita a 20 etiquetas de 40 caracteres como maximo. */
function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0 && tag.length <= 40)
    .slice(0, 20);
}

export function KnowledgeScreen({
  onNotice,
  canWrite,
}: {
  onNotice: (kind: NoticeKind, text: string) => void;
  /** Solo propietarios y administradores pueden crear o borrar (la API responde 403). */
  canWrite: boolean;
}): ReactElement {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    api
      .listKnowledge(controller.signal)
      .then((items) => setState({ status: 'ready', items }))
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setState({ status: 'error', message: errorMessage(error) });
      });
    return () => controller.abort();
  }, [reloadToken]);

  const handleCreate = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      const cleanTitle = title.trim();
      const cleanContent = content.trim();
      if (!cleanTitle || !cleanContent) {
        setFormError('El titulo y el contenido son obligatorios.');
        return;
      }
      setFormError(null);
      setCreating(true);
      api
        .createKnowledge({ title: cleanTitle, content: cleanContent, tags: parseTags(tags) })
        .then((created) => {
          setState((prev) =>
            prev.status === 'ready' ? { status: 'ready', items: [created, ...prev.items] } : prev,
          );
          setTitle('');
          setContent('');
          setTags('');
          onNotice('success', 'Documento anadido a la base de conocimiento.');
        })
        .catch((error: unknown) => setFormError(errorMessage(error)))
        .finally(() => setCreating(false));
    },
    [content, onNotice, tags, title],
  );

  const handleDelete = useCallback(
    (id: string): void => {
      setDeleting(id);
      api
        .deleteKnowledge(id)
        .then(() => {
          setState((prev) =>
            prev.status === 'ready'
              ? { status: 'ready', items: prev.items.filter((item) => item.id !== id) }
              : prev,
          );
          setPendingDelete(null);
          onNotice('success', 'Documento eliminado.');
        })
        .catch((error: unknown) => onNotice('error', errorMessage(error)))
        .finally(() => setDeleting(null));
    },
    [onNotice],
  );

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Base de conocimiento</h1>
          <p className="page__subtitle">
            Documentos que el agente consulta antes de responder: preguntas frecuentes, politicas,
            catalogos y procedimientos internos.
          </p>
        </div>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          <Icon name="refresh" />
          Actualizar
        </button>
      </header>

      <section className="card">
        <h2 className="card__title">Anadir documento</h2>

        {!canWrite ? (
          <div className="banner banner--warning" role="status">
            <Icon name="warning" className="banner__icon" />
            <p className="banner__text">
              Solo el propietario y los administradores pueden modificar la base de conocimiento.
            </p>
          </div>
        ) : null}

        <fieldset className="fieldset" disabled={!canWrite}>
        <form onSubmit={handleCreate}>
          <Field label="Titulo" htmlFor="kb-title" required hint="Maximo 300 caracteres.">
            <input
              id="kb-title"
              className="input"
              maxLength={300}
              value={title}
              placeholder="Politica de devoluciones"
              onChange={(event) => setTitle(event.target.value)}
              disabled={creating}
            />
          </Field>

          <Field
            label="Contenido"
            htmlFor="kb-content"
            required
            hint="Texto plano; cuanto mas concreto, mejor (maximo 50000 caracteres)."
          >
            <textarea
              id="kb-content"
              className="input input--tall"
              rows={6}
              maxLength={50000}
              value={content}
              onChange={(event) => setContent(event.target.value)}
              disabled={creating}
            />
          </Field>

          <Field
            label="Etiquetas"
            htmlFor="kb-tags"
            hint="Separadas por comas, hasta 20 etiquetas de 40 caracteres."
          >
            <input
              id="kb-tags"
              className="input"
              value={tags}
              onChange={(event) => setTags(event.target.value)}
              disabled={creating}
            />
          </Field>

          {formError ? (
            <div className="banner banner--error" role="alert">
              <Icon name="error" className="banner__icon" />
              <p className="banner__text">{formError}</p>
            </div>
          ) : null}

          <div className="page__actions page__actions--end">
            <button type="submit" className="button button--primary" disabled={creating}>
              {creating ? <Spinner label="Guardando…" /> : (<><Icon name="plus" />Anadir documento</>)}
            </button>
          </div>
        </form>
        </fieldset>
      </section>

      {state.status === 'loading' ? <SkeletonRows rows={4} /> : null}

      {state.status === 'error' ? (
        <ErrorBanner message={state.message} onRetry={() => setReloadToken((token) => token + 1)} />
      ) : null}

      {state.status === 'ready' && state.items.length === 0 ? (
        <EmptyState
          icon="knowledge"
          title="La base de conocimiento esta vacia"
          description="Anade el primer documento para que el agente pueda responder con informacion de tu negocio."
        />
      ) : null}

      {state.status === 'ready' && state.items.length > 0 ? (
        <div className="kb">
          {state.items.map((item) => (
            <article className="kb__item" key={item.id}>
              <header className="kb__header">
                <div>
                  <h3 className="kb__title">{item.title}</h3>
                  <p className="kb__meta">
                    Creado el {formatDateTime(item.createdAt)}
                    {item.tags.length > 0 ? ` · ${item.tags.join(', ')}` : ''}
                  </p>
                </div>
                <div className="kb__actions">
                  <Chip tone={item.isActive ? 'success' : 'neutral'}>
                    {item.isActive ? 'Activo' : 'Inactivo'}
                  </Chip>
                  {pendingDelete === item.id ? (
                    <>
                      <button
                        type="button"
                        className="button button--danger button--sm"
                        onClick={() => handleDelete(item.id)}
                        disabled={deleting === item.id}
                      >
                        {deleting === item.id ? <Spinner label="Eliminando…" /> : 'Confirmar borrado'}
                      </button>
                      <button
                        type="button"
                        className="button button--ghost button--sm"
                        onClick={() => setPendingDelete(null)}
                        disabled={deleting === item.id}
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="button button--ghost button--sm"
                      onClick={() => setPendingDelete(item.id)}
                      disabled={!canWrite}
                      title={canWrite ? undefined : 'Solo los administradores pueden borrar documentos'}
                    >
                      <Icon name="trash" />
                      Eliminar
                    </button>
                  )}
                </div>
              </header>
              <p className="kb__content">{item.content}</p>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}
